import { useTranslation } from 'react-i18next';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  duration?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  dismissible?: boolean;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  position: string;
  dismissible: boolean;
}

class ToastManager {
  private listeners: Set<(toasts: Toast[]) => void> = new Set();
  private toasts: Toast[] = [];
  private idCounter = 0;

  subscribe(listener: (toasts: Toast[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  private show(type: ToastType, message: string, options: ToastOptions = {}) {
    const toast: Toast = {
      id: `toast-${++this.idCounter}`,
      type,
      message,
      duration: options.duration ?? 5000,
      position: options.position ?? 'top-right',
      dismissible: options.dismissible ?? true,
    };

    this.toasts.push(toast);
    this.notify();

    if (toast.duration > 0) {
      setTimeout(() => this.dismiss(toast.id), toast.duration);
    }

    return toast.id;
  }

  success(message: string, options?: ToastOptions) {
    return this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions) {
    return this.show('error', message, options);
  }

  warning(message: string, options?: ToastOptions) {
    return this.show('warning', message, options);
  }

  info(message: string, options?: ToastOptions) {
    return this.show('info', message, options);
  }

  dismiss(id: string) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  dismissAll() {
    this.toasts = [];
    this.notify();
  }
}

export const toast = new ToastManager();
